import React, { useEffect, useState, useCallback, Suspense, lazy, useMemo } from "react";
import { useTheme, PageTitle } from "../../../../_start/layout/core";
import { useLocation } from "react-router";
import { db } from "../../../../config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  StepConfig,
  StepRole,
  STEP_CONFIGS,
  createStepThemeConfig,
  getDefaultThemeConfig,
  getStepConfig,
} from "./stepConfig";
import { stepDependencyMap } from "../../../../config/stepDependencies";
import { FallbackView } from "../../../../_start/partials";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import store from "../../../../setup/redux/Store";

interface GenericStepWrapperProps {
  stepNumber: number;
}

interface StepAssignmentState {
  assignedTo: string | null;
  assignedToEmail: string | null;
  assignedRole: StepRole | null;
}

interface CollaboratorEntry {
  uid: string;
  email: string;
  role: string;
}

/**
 * Generic wrapper component for step dashboard pages
 * Replaces 24 nearly-identical StepXDashboardWrapper components
 */
export function GenericStepWrapper({ stepNumber }: GenericStepWrapperProps) {
  const { setTheme } = useTheme();
  const config = getStepConfig(stepNumber);
  const location = useLocation();

  // Read project context from navigation state (set by StepsList / ProjectDashboardPage nav links)
  const locationState = (location.state as { projectID?: string; userID?: string } | null) ?? {};
  const projectID = locationState.projectID ?? null;
  const ownerUID = locationState.userID ?? null;
  const currentUID: string | null = store.getState().auth?.user?.uid ?? null;
  const isOwner = !!currentUID && currentUID === ownerUID;

  const [assignment, setAssignment] = useState<StepAssignmentState | null>(null);
  const [collaborators, setCollaborators] = useState<CollaboratorEntry[]>([]);
  const [isTeamProject, setIsTeamProject] = useState(false);
  const [saving, setSaving] = useState(false);
  // Dependency state: steps this step depends on that are not yet complete
  interface BlockingStepInfo {
    stepNumber: number;
    stepName: string;
    assignedToEmail: string | null;
  }
  const [blockedBySteps, setBlockedBySteps] = useState<BlockingStepInfo[]>([]);

  // Dynamically import the step page component
  const StepPage = useMemo(
    () =>
      lazy(
        () =>
          import(
            `./step${stepNumber}-dashboard/Step${stepNumber}DashboardPage`
          )
      ),
    [stepNumber]
  );

  // Apply theme configuration on mount
  useEffect(() => {
    const themeConfig = createStepThemeConfig(
      config?.showToolbar,
      config?.showSidebar,
      config?.sidebarContent
    );
    setTheme(themeConfig);

    return () => {
      setTheme(getDefaultThemeConfig());
    };
  }, [setTheme, config]);

  // Fetch team assignment data for this step
  const fetchAssignment = useCallback(async () => {
    if (!ownerUID || !projectID) return;
    try {
      // Read project doc for team info
      const projectRef = doc(db, "users", ownerUID, "projects", projectID);
      const projectSnap = await getDoc(projectRef);
      if (!projectSnap.exists()) return;
      const pd = projectSnap.data();
      const collabs: CollaboratorEntry[] = pd.collaborators || [];
      if (collabs.length > 0 || pd.teamId) {
        setIsTeamProject(true);
        setCollaborators(collabs);
      }

      // Read step progress doc for assignment
      const stepKey = `step${stepNumber}`;
      const stepRef = doc(db, "users", ownerUID, "projects", projectID, "progress", stepKey);
      const stepSnap = await getDoc(stepRef);
      if (stepSnap.exists()) {
        const sd = stepSnap.data();
        if (sd.assignedTo || sd.assignedRole) {
          setAssignment({
            assignedTo: sd.assignedTo ?? null,
            assignedToEmail: sd.assignedToEmail ?? null,
            assignedRole: sd.assignedRole ?? null,
          });
        }
      }

      // Check if this step has unmet dependencies (team projects only)
      const dep = stepDependencyMap[stepNumber];
      if (dep && (collabs.length > 0 || pd.teamId)) {
        const blocking: BlockingStepInfo[] = [];
        for (const parentStepNum of dep.dependsOn) {
          const parentKey = `step${parentStepNum}`;
          const parentRef = doc(db, "users", ownerUID, "projects", projectID, "progress", parentKey);
          const parentSnap = await getDoc(parentRef);
          const parentProgress = parentSnap.exists() ? (parentSnap.data().stepProgress || 0) : 0;
          if (parentProgress < 100) {
            const parentConfig = STEP_CONFIGS.find((s) => s.stepNumber === parentStepNum);
            const parentAssignedEmail = parentSnap.exists()
              ? (parentSnap.data().assignedToEmail ?? null)
              : null;
            blocking.push({
              stepNumber: parentStepNum,
              stepName: parentConfig?.pageTitle ?? `Step ${parentStepNum}`,
              assignedToEmail: parentAssignedEmail,
            });
          }
        }
        setBlockedBySteps(blocking);
      }
    } catch {
      // non-blocking
    }
  }, [ownerUID, projectID, stepNumber]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  // Reassign this step to a different team member (owner only)
  const handleReassign = useCallback(async (targetUID: string, targetEmail: string) => {
    if (!ownerUID || !projectID) return;
    setSaving(true);
    try {
      const stepKey = `step${stepNumber}`;
      const stepRef = doc(db, "users", ownerUID, "projects", projectID, "progress", stepKey);
      await updateDoc(stepRef, {
        assignedTo: targetUID || null,
        assignedToEmail: targetEmail || null,
        assignedRole: config?.defaultRole ?? null,
      });
      setAssignment({
        assignedTo: targetUID || null,
        assignedToEmail: targetEmail || null,
        assignedRole: config?.defaultRole ?? null,
      });
    } catch {
      // non-blocking
    } finally {
      setSaving(false);
    }
  }, [ownerUID, projectID, stepNumber, config?.defaultRole]);

  if (!config) {
    return <div>Step configuration not found for step {stepNumber}</div>;
  }

  // Build reassign options for owner
  const reassignOptions: Array<{ uid: string; email: string }> = isOwner
    ? [
        { uid: ownerUID ?? "", email: "Me (owner)" },
        ...collaborators.map((c) => ({ uid: c.uid, email: c.email })),
      ]
    : [];

  // Determine display name for assigned user
  const assigneeName = assignment?.assignedTo === ownerUID
    ? "You (owner)"
    : assignment?.assignedToEmail ?? null;

  const roleLabel = (r: StepRole | null) =>
    r === "strategist" ? "Strategy" : r === "executor" ? "Execution" : null;

  return (
    <>
      {/* Team assignment banner — only shown for team projects */}
      {isTeamProject && (
        <div
          style={{
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            padding: "10px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {/* Role badge */}
          {config.defaultRole && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: config.defaultRole === "strategist" ? "#1D4ED8" : "#065F46",
                background: config.defaultRole === "strategist" ? "#DBEAFE" : "#D1FAE5",
                padding: "3px 10px",
                borderRadius: "9999px",
              }}
            >
              {roleLabel(config.defaultRole)}
            </span>
          )}

          {/* Assigned to */}
          <span style={{ fontSize: "13px", color: "#475569" }}>
            {assigneeName ? (
              <>
                <i className="bi bi-person-fill me-1" style={{ color: "#94A3B8" }} />
                Assigned to: <strong>{assigneeName}</strong>
              </>
            ) : (
              <span style={{ color: "#94A3B8" }}>Unassigned</span>
            )}
          </span>

          {/* Reassign dropdown — owner only */}
          {isOwner && reassignOptions.length > 1 && (
            <div className="dropdown ms-auto">
              <button
                type="button"
                className="btn btn-sm btn-light dropdown-toggle"
                data-bs-toggle="dropdown"
                style={{ fontSize: "12px", padding: "4px 10px" }}
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner-border spinner-border-sm me-1" style={{ width: "10px", height: "10px" }} />
                ) : (
                  <i className="bi bi-person-gear me-1" />
                )}
                Reassign
              </button>
              <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ fontSize: "13px", minWidth: "180px" }}>
                {reassignOptions.map((opt) => (
                  <li key={opt.uid}>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleReassign(opt.uid, opt.email)}
                    >
                      {opt.uid === ownerUID ? (
                        <><i className="bi bi-person-fill me-2" />{opt.email}</>
                      ) : (
                        <><i className="bi bi-person me-2" />{opt.email}</>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Dependency info banner — only shown for team projects with unmet dependencies */}
      {isTeamProject && blockedBySteps.length > 0 && (
        <div
          style={{
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderLeft: "4px solid #F59E0B",
            borderRadius: "10px",
            padding: "10px 16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#92400E",
              marginBottom: "4px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="bi bi-info-circle-fill" style={{ color: "#F59E0B" }} />
            Step dependency
          </div>
          {blockedBySteps.map((b) => (
            <div key={b.stepNumber} style={{ fontSize: "13px", color: "#78350F", marginTop: "2px" }}>
              This step depends on Step {String(b.stepNumber).padStart(2, "0")}: <strong>{b.stepName}</strong>.
              {b.assignedToEmail ? (
                <>
                  {" "}
                  <i className="bi bi-person-fill" style={{ color: "#D97706", marginRight: "2px" }} />
                  <strong>{b.assignedToEmail}</strong> is working on it.
                </>
              ) : (
                <> That step is not yet complete.</>
              )}
            </div>
          ))}
        </div>
      )}

      <ErrorBoundary>
        <Suspense fallback={<FallbackView />}>
          <StepPage />
        </Suspense>
      </ErrorBoundary>
      <PageTitle>{config.pageTitle}</PageTitle>
    </>
  );
}

/**
 * Factory function to create step wrapper components
 * Use this to generate individual step wrappers that can be used in routes
 */
export function createStepWrapper(stepNumber: number) {
  return function StepWrapper() {
    return <GenericStepWrapper stepNumber={stepNumber} />;
  };
}

// Pre-generated step wrappers for backward compatibility with existing routes
export const GenericStep1Wrapper = createStepWrapper(1);
export const GenericStep2Wrapper = createStepWrapper(2);
export const GenericStep3Wrapper = createStepWrapper(3);
export const GenericStep4Wrapper = createStepWrapper(4);
export const GenericStep5Wrapper = createStepWrapper(5);
export const GenericStep6Wrapper = createStepWrapper(6);
export const GenericStep7Wrapper = createStepWrapper(7);
export const GenericStep8Wrapper = createStepWrapper(8);
export const GenericStep9Wrapper = createStepWrapper(9);
export const GenericStep10Wrapper = createStepWrapper(10);
export const GenericStep11Wrapper = createStepWrapper(11);
export const GenericStep12Wrapper = createStepWrapper(12);
export const GenericStep13Wrapper = createStepWrapper(13);
export const GenericStep14Wrapper = createStepWrapper(14);
export const GenericStep15Wrapper = createStepWrapper(15);
export const GenericStep16Wrapper = createStepWrapper(16);
export const GenericStep17Wrapper = createStepWrapper(17);
export const GenericStep18Wrapper = createStepWrapper(18);
export const GenericStep19Wrapper = createStepWrapper(19);
export const GenericStep20Wrapper = createStepWrapper(20);
export const GenericStep21Wrapper = createStepWrapper(21);
export const GenericStep22Wrapper = createStepWrapper(22);
export const GenericStep23Wrapper = createStepWrapper(23);
export const GenericStep24Wrapper = createStepWrapper(24);
